/** Kp and X-ray cards, the five latest SWPC alerts and eight merged DONKI events (loader-spaceweather.js). */
import { useAwareness } from '../../../awareness/store';
import { ErrorLine, Muted, SubHeading } from './Section';
import { dayMonthYear, hhmmZ } from './shared';

function Card({ label, value, color, detail }: { label: string; value: string; color?: string; detail: string }) {
  return (
    <div className="rounded-control border border-hairline bg-inset/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-ink-3">{label}</div>
      <div className="font-mono text-lg text-ink" style={color ? { color } : undefined}>
        {value}
      </div>
      <div className="text-[10px] text-ink-3">{detail}</div>
    </div>
  );
}

export function SpaceWeatherPanel() {
  const sw = useAwareness((s) => s.spaceWeather);
  if (!sw) return <Muted>Loading space weather.</Muted>;

  const kp = sw.kp;
  const xray = sw.xray;
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        {kp.error ? (
          <Card label="Kp index" value="--" detail="Feed unavailable" />
        ) : kp.data ? (
          <Card
            label="Kp index"
            value={kp.data.kp.toFixed(2)}
            color={kp.data.color}
            detail={kp.data.timeMs !== null ? `${kp.data.level}, ${hhmmZ(kp.data.timeMs)}` : kp.data.level}
          />
        ) : (
          <Card label="Kp index" value="--" detail="No recent reading" />
        )}
        {xray.error ? (
          <Card label="X-ray class" value="--" detail="Feed unavailable" />
        ) : xray.data ? (
          <Card
            label="X-ray class"
            value={xray.data.className}
            color={xray.data.color}
            detail={xray.data.flux === 0 ? 'No flux' : `GOES 0.1-0.8 nm, ${hhmmZ(xray.data.timeMs)}`}
          />
        ) : (
          <Card label="X-ray class" value="--" detail="Loading" />
        )}
      </div>

      <div>
        <SubHeading>Recent SWPC alerts</SubHeading>
        {sw.alerts.error ? (
          <ErrorLine text={`Alerts feed unavailable: ${sw.alerts.error}`} />
        ) : !sw.alerts.data || sw.alerts.data.length === 0 ? (
          <Muted>No recent alerts</Muted>
        ) : (
          <ul className="flex flex-col gap-1">
            {sw.alerts.data.map((a, i) => (
              <li key={`${a.productId}-${i}`} className="flex items-baseline gap-2 leading-snug">
                <span className="w-12 shrink-0 font-mono text-[10px] text-accent">{a.productId}</span>
                <span className="text-ink-2">{a.headline}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <SubHeading>DONKI events (last 7 d)</SubHeading>
        {sw.donki.error ? (
          <ErrorLine text={sw.donki.error} />
        ) : !sw.donki.data || sw.donki.data.length === 0 ? (
          <Muted>No CME, flare or storm events in the last 7 days</Muted>
        ) : (
          <ul className="flex flex-col gap-1">
            {sw.donki.data.map((ev, i) => (
              <li key={`${ev.type}-${ev.id}-${i}`} className="flex items-baseline gap-2 leading-snug">
                <span className="w-8 shrink-0 font-mono text-[10px]" style={{ color: ev.color }}>
                  {ev.type}
                </span>
                <span className="text-ink-2">
                  <span className="text-ink-3">{dayMonthYear(ev.timeMs)}</span> {ev.label || ev.id}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {sw.updatedMs !== null && <div className="text-[10px] text-ink-3">Updated {hhmmZ(sw.updatedMs)}, refreshes every 5 min</div>}
    </div>
  );
}
